import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  ModuleRole,
  CreateModuleRolePayload,
  UpdateModuleRolePayload,
  BulkSetModuleRolePayload,
  AdminInvitePayload,
  PaginatedResponse,
  Invitation,
  ActivityEntry,
  VerifyPasswordPayload,
  VerifyPasswordResponse,
  ModuleRoleLevel,
  BulkModuleRoleUpdateResponse,
} from '@/lib/types';

const MODULE_ROLES_API = '/api/v1/module-roles';

// ─── Filter Types ───────────────────────────────────────────────────────────

export interface ModuleRoleFilters {
  page?: number;
  page_size?: number;
  user?: number;
  module?: string;
}

function cleanFilters<T extends object>(filters: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );
}

// ─── Module Role Hooks ──────────────────────────────────────────────────────

export function useModuleRoles(
  filters: ModuleRoleFilters = {},
  options: { enabled?: boolean } = {},
) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.moduleRoles.list(clean),
    queryFn: async (): Promise<PaginatedResponse<ModuleRole>> => {
      const response = await apiClient.get<PaginatedResponse<ModuleRole>>(
        `${MODULE_ROLES_API}/`,
        { params: clean }
      );
      return response.data;
    },
    enabled: options.enabled ?? true,
  });
}

export function useAllModuleRoles() {
  return useQuery({
    queryKey: queryKeys.moduleRoles.list({ page_size: 1000 }),
    queryFn: async (): Promise<PaginatedResponse<ModuleRole>> => {
      const response = await apiClient.get<PaginatedResponse<ModuleRole>>(
        `${MODULE_ROLES_API}/`,
        { params: { page_size: 1000 } }
      );
      return response.data;
    },
  });
}

export async function fetchModuleRolesForUser(user: number): Promise<ModuleRole[]> {
  const response = await apiClient.get<PaginatedResponse<ModuleRole>>(
    `${MODULE_ROLES_API}/`,
    { params: { user, page_size: 100 } },
  );
  return response.data.results;
}

export function useCreateModuleRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateModuleRolePayload): Promise<ModuleRole> => {
      const response = await apiClient.post<ModuleRole>(`${MODULE_ROLES_API}/`, data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.moduleRoles.all });
    },
  });
}

export function useUpdateModuleRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdateModuleRolePayload;
    }): Promise<ModuleRole> => {
      const response = await apiClient.patch<ModuleRole>(
        `${MODULE_ROLES_API}/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.moduleRoles.all });
    },
  });
}

export function useBulkSetModuleRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: BulkSetModuleRolePayload): Promise<ModuleRole[]> => {
      const response = await apiClient.post<ModuleRole[]>(
        `${MODULE_ROLES_API}/bulk-set/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.moduleRoles.all });
    },
  });
}

export function useBulkUpdateModuleRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      user,
      roles,
    }: {
      user: number;
      roles: Record<string, ModuleRoleLevel>;
    }): Promise<BulkModuleRoleUpdateResponse> => {
      const response = await apiClient.put<BulkModuleRoleUpdateResponse>(
        `${MODULE_ROLES_API}/bulk/`,
        { user, roles },
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.moduleRoles.all });
    },
  });
}

// ─── Admin Activity Hook ────────────────────────────────────────────────────

const ADMIN_ACTIVITY_API = '/api/v1/admin/activity';

export interface AdminActivityFilters {
  page?: number;
  page_size?: number;
}

export function useAdminActivity(filters: AdminActivityFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.adminActivity.list(clean),
    queryFn: async (): Promise<PaginatedResponse<ActivityEntry>> => {
      const response = await apiClient.get<PaginatedResponse<ActivityEntry>>(
        `${ADMIN_ACTIVITY_API}/`,
        { params: clean }
      );
      return response.data;
    },
    refetchInterval: 30_000,
  });
}

// ─── Admin Invite Hook ──────────────────────────────────────────────────────

export function useAdminInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: AdminInvitePayload): Promise<Invitation> => {
      const response = await apiClient.post<Invitation>('/api/v1/invitations/', data);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.invitations.all });
      qc.invalidateQueries({ queryKey: queryKeys.techProfiles.all });
    },
  });
}

export function useVerifyPassword() {
  return useMutation({
    mutationFn: async (data: VerifyPasswordPayload): Promise<VerifyPasswordResponse> => {
      const response = await apiClient.post<VerifyPasswordResponse>(
        '/api/v1/auth/verify-password/',
        data
      );
      return response.data;
    },
  });
}
